import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BsCheck } from 'react-icons/bs';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useSortBy, useTable } from 'react-table';
import { Box, Button, Flex, HStack, Icon, Table, Tbody, Td, Text, Th, Thead, Tooltip, Tr } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import LoadingSpinner from '../../../../../../../../../../../../../../components/Spinner';
import { analyticsIds } from '../../../../../../../../../../../../../../constants/analyticsIds';
import { CypressIds } from '../../../../../../../../../../../../../../constants/cypressIds';
import { hideModal } from '../../../../../../../../../../../../../../redux/modules/modal';
import { useAllCompanyPolicyTypesData } from '../../../../../../../../../../../../../../services/company-service';
import { useDocumentExpirations, useSaveDocumentExpirations } from '../../../../../../../../../../../../../../services/document-expiration-service';
import { useUpdateAssociatedPolicies } from '../../../../../../../../../../../../../../services/document-service';
import { useActiveRecordDocuments, useActiveRecordPolicies } from '../../../../../../../../../../../../../../services/record-service';
import colors from '../../../../../../../../../../../../../../styles/theme/foundation/colors';
import { getDateObject, getDateString } from '../../../../../../../../../../../../../../utils/dateUtil';
import ExpirationRowsEditor from '../ExpirationRowsEditor';
import { buildRowsFromExpirations, diffExpirationRows, soonestDate } from '../ExpirationRowsEditor/util';

import { Columns, ColumnTypeCells } from './columns';

const rowTextStyle = {
  fontFamily: 'Poppins',
  fontStyle: 'normal',
  fontWeight: '400',
  lineHeight: '22px',
  letterSpacing: '-0.02em',
  textAlign: 'left',
  justifyContent: 'left',
  borderBottom: '1px solid #E8E8E8'
};

const sameIdSet = (a = [], b = []) => a.length === b.length && a.every(id => b.includes(id));

/**
 * One place to decide when a document expires: which active policies it is linked to, plus any
 * custom expirations. Both feed the same effective date, so they share one save.
 */
const DocumentExpirationDateModal = ({ document, recordId }) => {
  const dispatch = useDispatch();
  const { companyId } = useParams();
  const { id: docId, insurableId: insuredId } = document;

  const { data: policiesData, isLoading: policiesLoading } = useActiveRecordPolicies(recordId);
  const { data: documentsData } = useActiveRecordDocuments({ recordId });
  const { data: expirations, isFetched: expirationsFetched } = useDocumentExpirations({ docId });
  const { data: policyTypes = [] } = useAllCompanyPolicyTypesData(companyId, { enabled: !!companyId });

  const { mutateAsync: updateDocumentPolicies } = useUpdateAssociatedPolicies({ recordId, insuredId });
  const { mutateAsync: saveExpirations } = useSaveDocumentExpirations({ docId, recordId, insuredId });

  const currentDocument = useMemo(() => documentsData?.find(d => d.id === docId), [documentsData]);
  const linkedPolicies = useMemo(() => currentDocument?.policies?.map(p => p.id) ?? [], [currentDocument]);

  const [linkedPolicyIds, setLinkedPolicyIds] = useState([]);
  const [rows, setRows] = useState([]);
  const [isConfirmingCancel, setIsConfirmingCancel] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const originalRowsRef = useRef([]);
  const seededRef = useRef(false);

  useEffect(() => {
    setLinkedPolicyIds(linkedPolicies);
  }, [linkedPolicies]);

  // stage the saved expirations once; refetches after a save must not clobber in-progress edits
  useEffect(() => {
    if (seededRef.current || !expirationsFetched) return;

    seededRef.current = true;
    const seeded = buildRowsFromExpirations(expirations);
    originalRowsRef.current = seeded;
    setRows(seeded);
  }, [expirationsFetched]);

  const handlePolicyIdChange = ({ policyId, isChecked }) => {
    setLinkedPolicyIds(prev => (isChecked
      ? [...new Set([...prev, policyId])]
      : prev.filter(id => id !== policyId)));
  };

  const expirationDiff = useMemo(() => diffExpirationRows(rows, originalRowsRef.current), [rows]);
  const expirationsDirty = expirationDiff.creates.length + expirationDiff.updates.length + expirationDiff.deletes.length > 0;
  const policiesDirty = !sameIdSet(linkedPolicyIds, linkedPolicies);
  const isDirty = expirationsDirty || policiesDirty;
  const hasIncompleteRow = rows.some(row => !row.date);

  const effectiveDate = useMemo(() => {
    const checkedPolicyDates = (policiesData ?? [])
      .filter(policy => linkedPolicyIds.includes(policy.id))
      .map(policy => getDateObject(policy.expiresAt))
      .filter(Boolean);

    return soonestDate([...rows, ...checkedPolicyDates.map(date => ({ date }))]);
  }, [policiesData, linkedPolicyIds, rows]);

  const handleSave = async () => {
    setIsSaving(true);

    try {
      await Promise.all([
        ...(policiesDirty ? [updateDocumentPolicies({ document: currentDocument, policyIds: linkedPolicyIds })] : []),
        ...(expirationsDirty ? [saveExpirations(expirationDiff)] : [])
      ]);
    } catch (error) {
      setIsSaving(false);
      return;
    }

    dispatch(hideModal());
  };

  const handleCancel = () => {
    if (isDirty) {
      setIsConfirmingCancel(true);
      return;
    }

    dispatch(hideModal());
  };

  const tableData = useMemo(() => (policiesLoading ? [] : policiesData), [policiesData, policiesLoading]);
  const columns = useMemo(() => Columns.map(column => ColumnTypeCells[column]), []);

  const { getTableProps, getTableBodyProps, headerGroups, rows: tableRows, prepareRow } = useTable(
    {
      columns,
      data: tableData || [],
      manualSortBy: true,
      document: currentDocument,
      linkedPolicyIds,
      handlePolicyIdChange,
      recordId
    },
    useSortBy
  );

  const hasPolicies = !policiesLoading && policiesData?.length > 0;

  const policiesTable = useMemo(() => {
    if (policiesLoading) {
      return (
        <Box minH="100px">
          <LoadingSpinner isInline />
        </Box>
      );
    }

    return (
      <Table
        size="sm"
        variant="simple"
        w="100%"
        {...getTableProps()}
        className="AssociatedPoliciesContent"
      >
        <Thead>
          {headerGroups.map(headerGroup => (
            <Tr
              fontWeight="500"
              letterSpacing="-0.02em"
              lineHeight="18px"
              {...headerGroup.getHeaderGroupProps()}
            >
              {headerGroup.headers.map(column => (
                <Th
                  backgroundColor="greyScale.50"
                  color="greyScale.900"
                  justifyContent="center"
                  m={0}
                  p={1}
                  textAlign="center"
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  isNumeric={column.isNumeric}
                >
                  {column.render('Header')}
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody {...getTableBodyProps()}>
          {tableRows.map(row => {
            prepareRow(row);
            return (
              <Tr
                data-analytics-id={analyticsIds.insuredProfilePage.documentsComponent.selectPolicyToLink}
                style={{ ...rowTextStyle }}
                {...row.getRowProps()}
              >
                {row.cells.map(cell => (
                  <Td
                    justifyContent="center"
                    p={2}
                    textAlign="center"
                    {...cell.getCellProps()}
                    isNumeric={cell.column.isNumeric}
                  >
                    {cell.render('Cell')}
                  </Td>
                ))}
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    );
  }, [policiesLoading, policiesData, linkedPolicyIds]);

  if (!seededRef.current) {
    return <LoadingSpinner isInline minHeight="200px" />;
  }

  return (
    <Box className="DocumentExpirationDateModal" fontSize="md" textAlign="left">
      <Text color={colors.greyScale['700']} fontSize="17px" lineHeight="26px" mb={6}>
        {effectiveDate ? (
          <>
            The document will show
            {' '}
            <Text as="span" color={colors.greyScale['900']} fontWeight="semibold">
              {getDateString(effectiveDate, 'MM/DD/YYYY')}
            </Text>
            {' '}
            &mdash; the soonest to expire
            {' '}
            {hasPolicies ? 'of its linked policies and custom dates' : 'date'}
            .
          </>
        ) : (
          `The document will show no expiration date until ${hasPolicies ? 'a policy is linked or ' : ''}a date is added.`
        )}
      </Text>

      {(policiesLoading || hasPolicies) && (
        <>
          <Text color={colors.greyScale['900']} fontSize="18px" fontWeight="semibold" mb={2}>
            Linked policies
          </Text>
          <Box maxH="280px" mb={8} overflowY="auto">
            {policiesTable}
          </Box>
        </>
      )}

      <ExpirationRowsEditor
        heading="Custom expirations"
        policyTypes={policyTypes}
        rows={rows}
        setRows={setRows}
      />

      {isConfirmingCancel ? (
        <Flex alignItems="center" flexWrap="wrap" gap={4} justifyContent="space-between" mt={8}>
          <Text color={colors.greyScale['800']} flex="1 1 240px">
            Discard your unsaved changes?
          </Text>

          <HStack spacing={3}>
            <Button size="lg" variant="secondary" onClick={() => setIsConfirmingCancel(false)}>
              Keep Editing
            </Button>
            <Button size="lg" variant="secondaryError" onClick={() => dispatch(hideModal())}>
              Yes, Discard
            </Button>
          </HStack>
        </Flex>
      ) : (
        <HStack justifyContent="flex-end" mt={8} spacing={4}>
          <Button size="lg" variant="secondary" onClick={handleCancel}>
            Cancel
          </Button>

          <Tooltip isDisabled={!hasIncompleteRow} label={hasIncompleteRow ? 'You must add a date to each row' : ''}>
            <Button
              data-cy={CypressIds.confirmPolicyDateButton}
              isDisabled={!isDirty || hasIncompleteRow}
              isLoading={isSaving}
              leftIcon={<Icon as={BsCheck} boxSize="20px" />}
              size="lg"
              variant="primary"
              onClick={handleSave}
            >
              Save Changes
            </Button>
          </Tooltip>
        </HStack>
      )}
    </Box>
  );
};

DocumentExpirationDateModal.propTypes = {
  document: PropTypes.shape({
    id: PropTypes.number.isRequired,
    insurableId: PropTypes.number.isRequired
  }).isRequired,
  recordId: PropTypes.number.isRequired
};

export default DocumentExpirationDateModal;
